import { NavLink, useLocation } from 'react-router-dom';
import {
  Building2,
  Calculator,
  BarChart3,
  Layers,
  LogOut,
  LayoutDashboard,
} from 'lucide-react';
import clsx from 'clsx';
import type { AuthUser } from '../lib/api';

const ROLE_LABELS: Record<string, string> = {
  gia_admin: 'GIA Admin',
  application_admin: 'Application Admin',
  nbl_calculator_admin: 'New Business Admin',
  regional_admin: 'Regional Admin',
  global_cbl: 'Global CBL',
};

const NAV = [
  { to: '/', label: 'Home', icon: LayoutDashboard, end: true, adminOnly: true },
  { to: '/geoclients', label: 'Geoclients', icon: Building2 },
  { to: '/models', label: 'Models', icon: Layers, adminOnly: true },
  { to: '/reporting', label: 'Enterprise Reporting', icon: BarChart3, adminOnly: true },
];

function roleLabel(user: AuthUser): string {
  if (user.roles?.length) {
    return user.roles.map((r) => ROLE_LABELS[r] || r).join(' · ');
  }
  if (user.role) return ROLE_LABELS[user.role] || user.role;
  return 'No role';
}

export function Sidebar({ user, onLogout }: { user: AuthUser; onLogout: () => void }) {
  const location = useLocation();

  return (
    <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col h-full">
      <div className="px-5 py-5 flex items-center gap-3 border-b border-slate-100">
        <div className="w-9 h-9 rounded-lg bg-brand-gradient flex items-center justify-center text-white shadow-sm">
          <Calculator size={18} />
        </div>
        <div>
          <div className="font-semibold text-slate-900 leading-tight">Resource Calculator</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-wider">v2.0</div>
        </div>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-0.5">
        {NAV.filter((item) => !item.adminOnly || user.permissions.canAccessModelsAndReporting).map(
          (item) => {
            const isGroupActive =
              location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

            return (
              <div key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                      isActive || isGroupActive
                        ? 'bg-brand-purple-50 text-brand-purple-700'
                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50',
                    )
                  }
                >
                  <item.icon size={16} />
                  <span className="flex-1">{item.label}</span>
                </NavLink>
              </div>
            );
          },
        )}
      </nav>
      <div className="px-5 py-4 border-t border-slate-100">
        <div className="text-xs font-medium text-slate-700 truncate">{user.displayName}</div>
        <div className="text-[11px] text-slate-400 truncate mt-0.5">{user.email}</div>
        <div className="text-[11px] text-brand-purple-700 mt-1">{roleLabel(user)}</div>
        <button onClick={onLogout} className="btn-ghost text-xs mt-3 w-full justify-start px-0">
          <LogOut size={14} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
